import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Inbox, RefreshCw, ShieldAlert, Siren, UserCheck, Users } from 'lucide-react'

import { Button } from '../components/ui/button'
import { SegmentedControl, StatTile } from '../components/ui/queueUi'
import { useAuth } from '../hooks/useAuth'
import {
  AgeIndicator,
  InitialsAvatar,
  QueueBadge,
  ReasonBadge,
  StatusBadge,
  TargetTypeIcon,
  TARGET_LABELS,
  caseRef,
  hoursSince,
  queueRailClass
} from '../components/moderation/moderationUi'
import {
  assignModerationCase,
  getModerationCases,
  type ModerationCase,
  type ModerationCaseStatus,
  type ModerationQueueType
} from '../lib/api/moderation'

const statusFilters: Array<{ value: ModerationCaseStatus | 'all'; label: string }> = [
  { value: 'Open', label: 'Open' },
  { value: 'Closed', label: 'Closed' },
  { value: 'all', label: 'All' }
]

const queueFilters: Array<{ value: ModerationQueueType | 'all'; label: string }> = [
  { value: 'all', label: 'All queues' },
  { value: 'PRIORITY', label: 'Priority' },
  { value: 'STANDARD', label: 'Standard' },
  { value: 'APPEALS', label: 'Appeals' }
]

type AssignmentFilter = 'all' | 'unassigned' | 'mine'

const assignmentFilters: Array<{ value: AssignmentFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'mine', label: 'Mine' }
]

export const SupportModerationPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [cases, setCases] = useState<ModerationCase[]>([])
  const [statusFilter, setStatusFilter] = useState<ModerationCaseStatus | 'all'>('Open')
  const [queueFilter, setQueueFilter] = useState<ModerationQueueType | 'all'>('all')
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('all')
  const [oldestFirst, setOldestFirst] = useState(true)
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [claimingId, setClaimingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadCases = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getModerationCases({ status: statusFilter, queue: queueFilter, limit: 100 })
      setCases(data.cases)
      setCursor(data.cursor ?? null)
      setHasMore(data.hasMore)
    } catch (err) {
      console.error('Failed to load moderation cases', err)
      setError('Unable to load moderation cases.')
    } finally {
      setLoading(false)
    }
  }, [queueFilter, statusFilter])

  useEffect(() => {
    void loadCases()
  }, [loadCases])

  const loadMore = async () => {
    if (!cursor || loadingMore) return
    setLoadingMore(true)
    try {
      const data = await getModerationCases({ status: statusFilter, queue: queueFilter, limit: 100, cursor })
      setCases((current) => [...current, ...data.cases.filter((item) => !current.some((existing) => existing.id === item.id))])
      setCursor(data.cursor ?? null)
      setHasMore(data.hasMore)
    } catch (err) {
      console.error('Failed to load more moderation cases', err)
      setError('Unable to load more cases.')
    } finally {
      setLoadingMore(false)
    }
  }

  const handleClaim = async (moderationCase: ModerationCase) => {
    if (!user || claimingId) return
    setClaimingId(moderationCase.id)
    try {
      const updated = await assignModerationCase(moderationCase.id, user.id)
      setCases((current) =>
        current.map((item) =>
          item.id === updated.id ? { ...item, ...updated, targetSnippet: item.targetSnippet, targetOwnerUsername: item.targetOwnerUsername } : item
        )
      )
    } catch (err) {
      console.error('Failed to claim moderation case', err)
      setError('Unable to claim that case.')
    } finally {
      setClaimingId(null)
    }
  }

  const visibleCases = useMemo(() => {
    let filtered = cases
    if (assignmentFilter === 'unassigned') {
      filtered = filtered.filter((item) => !item.assignedToUserId)
    } else if (assignmentFilter === 'mine') {
      filtered = filtered.filter((item) => item.assignedToUserId === user?.id)
    }

    const queueRank = (queue: ModerationQueueType) => (queue === 'PRIORITY' ? 0 : queue === 'APPEALS' ? 1 : 2)
    return [...filtered].sort((a, b) => {
      const byQueue = queueRank(a.queue) - queueRank(b.queue)
      if (byQueue !== 0) return byQueue
      const byAge = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      return oldestFirst ? byAge : -byAge
    })
  }, [assignmentFilter, cases, oldestFirst, user?.id])

  const stats = useMemo(() => {
    const open = cases.filter((item) => item.status === 'Open')
    return {
      open: open.length,
      priority: open.filter((item) => item.queue === 'PRIORITY').length,
      appeals: open.filter((item) => item.queue === 'APPEALS').length,
      unassigned: open.filter((item) => !item.assignedToUserId).length,
      overdue: open.filter((item) => hoursSince(item.createdAt) >= 24).length
    }
  }, [cases])

  return (
    <div className="space-y-4 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            Moderation Queue
          </h1>
          <p className="text-sm text-muted-foreground">
            Review reported content, claim a case, and open it for the full report history.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadCases()} disabled={loading}>
          <RefreshCw className={`mr-1 h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <StatTile label="Open cases" value={stats.open} icon={<Inbox className="h-5 w-5" />} onClick={() => { setStatusFilter('Open'); setQueueFilter('all'); setAssignmentFilter('all') }} />
        <StatTile label="Priority" value={stats.priority} icon={<Siren className="h-5 w-5" />} tone="danger" active={queueFilter === 'PRIORITY'} onClick={() => { setStatusFilter('Open'); setQueueFilter('PRIORITY') }} />
        <StatTile label="Appeals" value={stats.appeals} icon={<Users className="h-5 w-5" />} tone="indigo" active={queueFilter === 'APPEALS'} onClick={() => { setStatusFilter('Open'); setQueueFilter('APPEALS') }} />
        <StatTile label="Unassigned" value={stats.unassigned} icon={<UserCheck className="h-5 w-5" />} tone="amber" active={assignmentFilter === 'unassigned'} onClick={() => { setStatusFilter('Open'); setAssignmentFilter(assignmentFilter === 'unassigned' ? 'all' : 'unassigned') }} />
        <StatTile label="Waiting 24h+" value={stats.overdue} icon={<ArrowDownWideNarrow className="h-5 w-5" />} tone={stats.overdue > 0 ? 'danger' : 'default'} onClick={() => { setStatusFilter('Open'); setOldestFirst(true) }} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl options={statusFilters} value={statusFilter} onChange={setStatusFilter} />
        <SegmentedControl options={queueFilters} value={queueFilter} onChange={setQueueFilter} />
        <SegmentedControl options={assignmentFilters} value={assignmentFilter} onChange={setAssignmentFilter} />
        <button
          type="button"
          onClick={() => setOldestFirst((current) => !current)}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
        >
          {oldestFirst ? <ArrowUpNarrowWide className="h-3.5 w-3.5" /> : <ArrowDownWideNarrow className="h-3.5 w-3.5" />}
          {oldestFirst ? 'Oldest first' : 'Newest first'}
        </button>
      </div>

      {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading && <p className="p-6 text-center text-sm text-muted-foreground">Loading moderation cases…</p>}
      {!loading && visibleCases.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <Inbox className="mx-auto mb-3 h-8 w-8 opacity-40" />
          <p className="text-sm">No moderation cases match these filters. Nice and quiet out there.</p>
        </div>
      )}

      <div className="space-y-2">
        {visibleCases.map((moderationCase) => (
          <button
            key={moderationCase.id}
            type="button"
            onClick={() => navigate(`/support/moderation/${moderationCase.id}`)}
            className={`group flex w-full items-start gap-4 rounded-xl border border-l-4 border-border/60 bg-card p-4 text-left transition hover:border-primary/40 hover:shadow-elevation-1 ${queueRailClass(moderationCase.queue)}`}
          >
            <div className="flex w-14 shrink-0 flex-col items-start gap-1 pt-0.5">
              <AgeIndicator createdAt={moderationCase.createdAt} status={moderationCase.status} />
              <span className="font-mono text-[11px] text-muted-foreground">{caseRef(moderationCase.id)}</span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <QueueBadge queue={moderationCase.queue} />
                {moderationCase.status === 'Closed' && <StatusBadge status={moderationCase.status} />}
                <ReasonBadge reason={moderationCase.highestReasonCode} />
                {moderationCase.suspectedBrigade && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">
                    Possible brigade
                  </span>
                )}
              </div>

              <p className="flex items-center gap-2 text-sm text-foreground">
                <TargetTypeIcon targetType={moderationCase.targetType} className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  <span className="font-semibold">{TARGET_LABELS[moderationCase.targetType]}</span>
                  {moderationCase.targetOwnerUsername ? (
                    <span className="text-muted-foreground"> by @{moderationCase.targetOwnerUsername}</span>
                  ) : null}
                  {moderationCase.targetSnippet ? (
                    <span className="text-muted-foreground"> — “{moderationCase.targetSnippet}”</span>
                  ) : null}
                </span>
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                {moderationCase.reportCount24h} report{moderationCase.reportCount24h === 1 ? '' : 's'} in 24h
                {' · '}weighted {moderationCase.weightedReports24h.toFixed(1)}
                {moderationCase.status === 'Closed' && moderationCase.decision ? ` · resolved: ${moderationCase.decision}` : ''}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2 self-center">
              {moderationCase.assignedToUserId ? (
                <span className="flex items-center gap-2 text-xs text-muted-foreground" title={`Assigned to ${moderationCase.assignedToName ?? 'staff member'}`}>
                  <InitialsAvatar name={moderationCase.assignedToName} size="h-6 w-6" />
                  <span className="hidden max-w-32 truncate md:inline">
                    {moderationCase.assignedToUserId === user?.id ? 'You' : moderationCase.assignedToName}
                  </span>
                </span>
              ) : moderationCase.status === 'Open' ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={claimingId === moderationCase.id}
                  onClick={(event) => {
                    event.stopPropagation()
                    void handleClaim(moderationCase)
                  }}
                >
                  {claimingId === moderationCase.id ? 'Claiming…' : 'Claim'}
                </Button>
              ) : null}
            </div>
          </button>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more cases'}
          </Button>
        </div>
      )}
    </div>
  )
}
